import { type ColumnDef, createColumnHelper } from "@tanstack/react-table";

export interface StudentProgress {
  paid: number;
  completed: number;
  incomplete: number;
  rate: number;
}

export interface StudentRow {
  id: string;
  full_name: string;
  email: string;
  phone: string;
  joined: string;
  verified: boolean;
  progress: StudentProgress;
  impersonate_url: string;
  status: "unverified" | "active" | "started" | "joined";
}

const col = createColumnHelper<StudentRow>();

function initials(name: string) {
  return name
    .split(" ")
    .slice(0, 2)
    .map((n) => n[0] ?? "")
    .join("")
    .toUpperCase();
}

function StatusBadge({ status }: { status: StudentRow["status"] }) {
  const config = {
    unverified: {
      dot: "bg-amber-500",
      badge: "bg-amber-500/10 text-amber-500 border-amber-500/20",
      label: "Unverified",
    },
    active: {
      dot: "bg-emerald-500",
      badge: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20",
      label: "Active",
    },
    started: {
      dot: "bg-sky-500",
      badge: "bg-sky-500/10 text-sky-500 border-sky-500/20",
      label: "Started",
    },
    joined: {
      dot: "bg-muted-foreground",
      badge: "bg-secondary text-muted-foreground border-border",
      label: "Joined",
    },
  };
  const c = config[status];
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-[0.7rem] font-medium px-2 py-0.5 rounded-md border ${c.badge}`}
    >
      <span className={`size-1.5 rounded-full shrink-0 ${c.dot}`} />
      {c.label}
    </span>
  );
}

function ProgressRing({ rate }: { rate: number }) {
  const radius = 9;
  const circ = 2 * Math.PI * radius;
  const offset = circ - (circ * rate) / 100;
  return (
    <div className="inline-flex items-center gap-2">
      <svg
        width="22"
        height="22"
        viewBox="0 0 22 22"
        className="shrink-0 -rotate-90"
      >
        <circle
          cx="11"
          cy="11"
          r="9"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className="text-secondary"
        />
        <circle
          cx="11"
          cy="11"
          r="9"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeDasharray={circ}
          strokeDashoffset={offset}
          className="text-foreground"
        />
      </svg>
      <span className="text-xs font-medium text-foreground tabular-nums">
        {rate}%
      </span>
    </div>
  );
}

type PushEvent = (event: string, payload: object) => void;

export function makeStudentColumns(
  pushEvent: PushEvent,
): ColumnDef<StudentRow, any>[] {
  return [
    col.accessor("full_name", {
      header: "Full name",
      cell: (info) => {
        const row = info.row.original;
        return (
          <div className="flex items-center gap-2.5">
            <div className="size-7 rounded-md bg-secondary border border-border flex items-center justify-center shrink-0">
              <span className="text-[0.6rem] font-semibold text-foreground">
                {initials(row.full_name)}
              </span>
            </div>
            <div className="min-w-0">
              <p className="text-[0.8125rem] font-medium text-foreground truncate leading-tight">
                {row.full_name}
              </p>
              <p className="text-[0.68rem] text-muted-foreground truncate">
                {row.email}
              </p>
            </div>
          </div>
        );
      },
    }),
    col.accessor("status", {
      header: "Status",
      cell: (info) => <StatusBadge status={info.getValue()} />,
    }),
    col.accessor("phone", {
      header: "Phone",
      cell: (info) => (
        <span className="text-xs font-mono text-muted-foreground tabular-nums">
          {info.getValue()}
        </span>
      ),
    }),
    col.accessor("progress", {
      header: "Progress",
      cell: (info) => <ProgressRing rate={info.getValue().rate} />,
      sortingFn: (a, b) => a.original.progress.rate - b.original.progress.rate,
    }),
    col.accessor("joined", {
      header: "Joined",
      cell: (info) => (
        <span className="text-xs text-muted-foreground tabular-nums">
          {info.getValue()}
        </span>
      ),
    }),
    col.display({
      id: "actions",
      header: "Actions",
      size: 180,
      cell: (info) => {
        const row = info.row.original;
        return (
          <div className="flex items-center gap-2">
            <a
              href={row.impersonate_url}
              onClick={(e) => {
                e.preventDefault();
                const form = document.createElement("form");
                form.method = "POST";
                form.action = row.impersonate_url;
                const csrf = document.querySelector<HTMLMetaElement>(
                  "meta[name='csrf-token']",
                )?.content;
                if (csrf) {
                  const inp = document.createElement("input");
                  inp.type = "hidden";
                  inp.name = "_csrf_token";
                  inp.value = csrf;
                  form.appendChild(inp);
                }
                document.body.appendChild(form);
                form.submit();
              }}
              className="inline-flex items-center gap-1 h-7 px-2.5 rounded-md text-[0.7rem] font-medium text-sky-500 hover:bg-sky-500/10 transition-colors cursor-pointer"
            >
              View
            </a>
            <button
              onClick={() => info.row.toggleExpanded()}
              className="inline-flex items-center gap-1 h-7 px-2.5 rounded-md text-[0.7rem] font-medium text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
            >
              {info.row.getIsExpanded() ? "↑ Hide" : "↓ Details"}
            </button>
          </div>
        );
      },
    }),
  ];
}

export function StudentDetailPanel({ student }: { student: StudentRow }) {
  const p = student.progress;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      <div className="bg-card rounded-md border border-border p-4">
        <p className="text-xs font-medium text-muted-foreground mb-2">Paid Sessions</p>
        <p className="text-2xl font-semibold text-foreground">{p.paid}</p>
        <p className="text-[0.7rem] text-muted-foreground mt-0.5">
          KES {p.paid * 50} spent
        </p>
      </div>
      <div className="bg-card rounded-md border border-border p-4">
        <p className="text-xs font-medium text-muted-foreground mb-2">Completed</p>
        <p className="text-2xl font-semibold text-emerald-500">{p.completed}</p>
        <p className="text-[0.7rem] text-muted-foreground mt-0.5">videos finished</p>
      </div>
      <div className="bg-card rounded-md border border-border p-4">
        <p className="text-xs font-medium text-muted-foreground mb-2">Incomplete</p>
        <p className="text-2xl font-semibold text-amber-500">{p.incomplete}</p>
        <p className="text-[0.7rem] text-muted-foreground mt-0.5">sessions expired</p>
      </div>
      <div className="bg-card rounded-md border border-border p-4">
        <p className="text-xs font-medium text-muted-foreground mb-2">Completion Rate</p>
        <p className="text-2xl font-semibold text-indigo-500">{p.rate}%</p>
        <div className="mt-2 h-1.5 bg-secondary rounded-md overflow-hidden">
          <div
            className="h-full bg-indigo-500 rounded-md"
            style={{ width: `${p.rate}%` }}
          />
        </div>
      </div>
    </div>
  );
}
